"use client";

import { useCallback, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  Trash2,
  Upload,
} from "lucide-react";
import { parseCandidateInfo } from "./parseCandidate";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
]);

export const CANDIDATE_PROFILE_TEMPLATE = `CANDIDATE NAME:

PROFESSIONAL SUMMARY:
See resume

SKILLS:
See resume

WORK EXPERIENCE:
See resume

PROJECTS:
See resume

LEADERSHIP AND STAKEHOLDER EXAMPLES:
See resume

TECHNICAL EXAMPLES:
See resume

CHALLENGES AND FAILURES:
See resume

ACHIEVEMENTS:
See resume

CERTIFICATIONS:
See resume

EDUCATION:
See resume

INTERVIEW INSTRUCTIONS:
See resume`;

function candidateProfileWithName(profile, candidateName) {
  const current = String(profile || "");
  if (!current) return "";
  const name = String(candidateName || "").trim();
  if (!name) return current;
  return current.replace(/^CANDIDATE NAME:.*$/m, `CANDIDATE NAME: ${name}`);
}

function isAcceptedFile(file) {
  if (!file) return false;
  if (ACCEPTED_TYPES.has(file.type)) return true;
  const name = (file.name || "").toLowerCase();
  return (
    name.endsWith(".pdf") ||
    name.endsWith(".docx") ||
    name.endsWith(".txt")
  );
}

/**
 * STEP 1 — Fresh résumé/profile upload.
 *
 * The raw File is used only in this browser while text is extracted. It is
 * never uploaded to Jobolar storage or attached to a saved résumé slot.
 * Only the extracted text is kept in React state for the current setup flow.
 */
export default function ResumeSetupStep({ t, value, onChange, notice }) {
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const patch = useCallback(
    (fields) => onChange({ ...value, ...fields }),
    [onChange, value]
  );

  const handleFile = useCallback(
    async (file) => {
      setError(null);
      if (!isAcceptedFile(file)) {
        setError(t.upload_error_type);
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(t.upload_error_size);
        return;
      }

      setExtracting(true);
      try {
        const { extractResumeText } = await import("@/utils/extractResumeText");
        const text = (await extractResumeText(file)).slice(0, 20000).trim();
        if (!text) {
          setError(t.extract_failed);
          return;
        }

        const info = parseCandidateInfo(text);
        const baseName = (file.name || "resume").replace(/\.(pdf|docx|txt)$/i, "");
        onChange({
          source: "upload",
          slot: null,
          name: baseName,
          fileUrl: "",
          text,
          candidateName: info.name,
          candidateEmail: info.email,
          candidatePhone: info.phone,
          candidateProfile: candidateProfileWithName(
            value?.candidateProfile,
            info.name
          ),
          confirmed: false,
        });
      } catch {
        setError(t.extract_failed);
      } finally {
        setExtracting(false);
      }
    },
    [onChange, t, value?.candidateProfile]
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragOver(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const handlePastedText = useCallback(
    (text) => {
      const limitedText = text.slice(0, 20000);
      const info = parseCandidateInfo(limitedText);
      onChange({
        ...value,
        source: "paste",
        slot: null,
        fileUrl: "",
        text: limitedText,
        name: value?.name || t.pasted_resume_name,
        candidateName: value?.candidateName || info.name,
        candidateEmail: value?.candidateEmail || info.email,
        candidatePhone: value?.candidatePhone || info.phone,
        candidateProfile: candidateProfileWithName(
          value?.candidateProfile,
          value?.candidateName || info.name
        ),
        confirmed: false,
      });
    },
    [onChange, t, value]
  );

  const clearResume = useCallback(() => {
    onChange({
      source: null,
      slot: null,
      name: "",
      fileUrl: "",
      text: "",
      candidateName: "",
      candidateEmail: "",
      candidatePhone: "",
      candidateProfile: "",
      confirmed: false,
    });
  }, [onChange]);

  const hasResume = Boolean(value?.text);
  const busy = extracting;

  return (
    <div className="space-y-4">
      <p className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
        {notice || t.fresh_resume_notice}
      </p>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-slate-800">
          {t.paste_resume_label}
        </span>
        <textarea
          value={value?.text || ""}
          onChange={(event) => handlePastedText(event.target.value)}
          placeholder={t.paste_resume_placeholder}
          rows={10}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />
        <span className="mt-1 block text-xs text-slate-500">
          {t.paste_resume_note}
        </span>
      </label>

      <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-slate-400">
        <span className="h-px flex-1 bg-slate-200" />
        {t.or_upload_file}
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
          dragOver
            ? "border-indigo-500 bg-indigo-50"
            : "border-slate-300 bg-slate-50"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.txt,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleFile(file);
            event.target.value = "";
          }}
        />

        {extracting ? (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.extracting_text}
          </div>
        ) : (
          <>
            <Upload className="h-8 w-8 text-slate-400" />
            <p className="text-sm text-slate-600">
              {t.upload_drop_hint}{" "}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-semibold text-indigo-600 underline hover:text-indigo-700"
              >
                {t.upload_browse}
              </button>
            </p>
            <p className="text-xs text-slate-500">{t.upload_accept_hint}</p>
          </>
        )}
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-semibold text-slate-800">
          {t.candidate_profile_label} ({t.optional})
        </span>
        <textarea
          value={value?.candidateProfile || ""}
          placeholder={CANDIDATE_PROFILE_TEMPLATE}
          onChange={(event) =>
            patch({
              candidateProfile: event.target.value.slice(0, 20000),
              confirmed: false,
            })
          }
          rows={18}
          className="w-full rounded-xl border border-slate-300 px-4 py-3 font-mono text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
        />
        <span className="mt-1 block text-xs text-slate-500">
          {t.candidate_profile_hint}
        </span>
      </label>

      {hasResume && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {t.candidate_preview_title}
              </p>
              <p className="text-xs text-slate-500">
                {t.candidate_preview_hint}
              </p>
            </div>
            <button
              type="button"
              onClick={clearResume}
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t.remove_resume_btn}
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                {t.resume_name_label}
              </span>
              <input
                type="text"
                value={value.name}
                onChange={(event) =>
                  patch({ name: event.target.value, confirmed: false })
                }
                placeholder={t.resume_name_placeholder}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                {t.candidate_name_label}
              </span>
              <input
                type="text"
                value={value.candidateName}
                onChange={(event) =>
                  patch({ candidateName: event.target.value, confirmed: false })
                }
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                {t.candidate_email_label}
              </span>
              <input
                type="email"
                value={value.candidateEmail}
                onChange={(event) =>
                  patch({ candidateEmail: event.target.value, confirmed: false })
                }
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-slate-600">
                {t.candidate_phone_label}
              </span>
              <input
                type="tel"
                value={value.candidatePhone}
                onChange={(event) =>
                  patch({ candidatePhone: event.target.value, confirmed: false })
                }
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </label>
          </div>

          <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <summary className="cursor-pointer text-xs font-medium text-slate-600">
              {t.resume_text_label}
            </summary>
            <p className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap text-xs text-slate-600">
              {value.text.slice(0, 4000)}
            </p>
          </details>

          <div className="flex items-center justify-end gap-3">
            {value.confirmed ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                <CheckCircle className="h-4 w-4" />
                {t.resume_ready_badge}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => patch({ confirmed: true })}
                disabled={busy}
                className="rounded-full bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {t.confirm_resume_btn}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
